import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import {
    Button,
    Checkbox,
    CircularProgress,
    Divider,
    FormControlLabel,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography,
} from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';

import {
    createNotificationChannel,
    createNotificationEventType,
    deleteNotificationChannel,
    getNotificationChannels,
    getNotificationEventTypes,
    updateNotificationChannel,
} from '../../api';
import { checkStatus, parseJSON, handleError } from '../../../shared/api/core/common';
import { Toast, enqueueSnackbar, ToastSuccess, ToastError } from '../../../shared/components/Notifier';
import { NO_PERMISSIONS_ERROR_MSG } from '../../../shared/lib/Localization';

const EVENT_TYPE = 'STOCK_ORDER_SUPPLIER_NOTIFICATION';
const CHANNEL_TYPES = ['EMAIL', 'WEBHOOK'];
const ATTACHMENT_TYPES = ['PDF', 'EXCEL', 'PLAINTEXT'];

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: { padding: theme.spacing(2) },
        channelCard: {
            padding: theme.spacing(2),
            marginBottom: theme.spacing(2),
            border: '1px solid #e0e0e0',
        },
        field: { marginBottom: theme.spacing(2) },
        row: { display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' },
        actionsRow: { marginTop: theme.spacing(1), display: 'flex', gap: 8 },
    }),
);

interface ChannelDraft {
    id?: number;
    channel: string;
    enabled: boolean;
    sender: string;
    to: string;
    attachmentTypes: string[];
    subjectTemplate: string;
    bodyTemplate: string;
}

const emptyDraft = (): ChannelDraft => ({
    channel: 'EMAIL',
    enabled: true,
    sender: '',
    to: '',
    attachmentTypes: [],
    subjectTemplate: '',
    bodyTemplate: '',
});

const channelToDraft = (channel: any): ChannelDraft => {
    const config = channel.config || {};
    const toList = Array.isArray(config.to) ? config.to : [];
    return {
        id: channel.id,
        channel: channel.channel || 'EMAIL',
        enabled: channel.enabled !== false,
        sender: config.sender || '',
        to: toList.join('\n'),
        attachmentTypes: config.attachment_types || [],
        subjectTemplate: channel.subject_template || '',
        bodyTemplate: channel.body_template || '',
    };
};

const SupplierNotificationConfigScreen = (props: any) => {
    const classes = useStyles({});
    const [eventTypeId, setEventTypeId] = useState<number | null>(null);
    const [drafts, setDrafts] = useState<ChannelDraft[]>([]);
    const [isLoading, setLoading] = useState(false);
    const [savingIndex, setSavingIndex] = useState<number | null>(null);

    const businessId = props.userData?.selectedBusiness?.business_id;

    const showToast = (message: string, options = ToastError) => {
        props.showToast({ message, options });
    };

    const loadData = () => {
        if (!businessId) {
            return;
        }
        setLoading(true);
        getNotificationEventTypes(businessId, EVENT_TYPE)
            .catch(handleError)
            .then(checkStatus)
            .then(parseJSON)
            .then((data: any) => {
                const results = (data && data.results) || data || [];
                setEventTypeId(results.length > 0 ? results[0].id : null);
                return getNotificationChannels(businessId, EVENT_TYPE);
            })
            .then(checkStatus)
            .then(parseJSON)
            .then((data: any) => {
                const results = (data && data.results) || data || [];
                setDrafts(results.map(channelToDraft));
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
                showToast('Failed to load supplier notification configuration.');
            });
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [businessId]);

    const ensureEventType = (): Promise<number> => {
        if (eventTypeId) {
            return Promise.resolve(eventTypeId);
        }
        return createNotificationEventType({ event_type: EVENT_TYPE, business: businessId })
            .then(checkStatus)
            .then(parseJSON)
            .then((data: any) => {
                setEventTypeId(data.id);
                return data.id;
            });
    };

    const updateDraft = (index: number, patch: Partial<ChannelDraft>) => {
        setDrafts((prev) => prev.map((d, i) => (i === index ? { ...d, ...patch } : d)));
    };

    const toggleAttachmentType = (index: number, type: string) => {
        setDrafts((prev) => prev.map((d, i) => {
            if (i !== index) {
                return d;
            }
            const has = d.attachmentTypes.indexOf(type) > -1;
            return {
                ...d,
                attachmentTypes: has
                    ? d.attachmentTypes.filter((t) => t !== type)
                    : [...d.attachmentTypes, type],
            };
        }));
    };

    const handleAddChannel = () => {
        setDrafts((prev) => [...prev, emptyDraft()]);
    };

    const buildPayload = (draft: ChannelDraft, resolvedEventTypeId: number) => ({
        event_type: resolvedEventTypeId,
        channel: draft.channel,
        enabled: draft.enabled,
        subject_template: draft.subjectTemplate,
        body_template: draft.bodyTemplate,
        config: {
            sender: draft.sender,
            to: draft.to.split('\n').map((s) => s.trim()).filter(Boolean),
            cc: [],
            bcc: [],
            attachment_types: draft.attachmentTypes,
        },
    });

    const handleSaveChannel = (index: number) => {
        const draft = drafts[index];
        setSavingIndex(index);
        ensureEventType()
            .then((resolvedEventTypeId) => {
                const payload = buildPayload(draft, resolvedEventTypeId);
                return draft.id
                    ? updateNotificationChannel(draft.id, payload)
                    : createNotificationChannel(payload);
            })
            .then(checkStatus)
            .then(parseJSON)
            .then(() => {
                setSavingIndex(null);
                showToast('Notification channel saved.', ToastSuccess);
                loadData();
            })
            .catch((error: any) => {
                setSavingIndex(null);
                let msg = error && error.body && error.body.error
                    ? error.body.error
                    : 'Failed to save notification channel.';
                if (error && (error.status === 403 || error.status === 401)) {
                    msg = NO_PERMISSIONS_ERROR_MSG;
                }
                showToast(msg);
            });
    };

    const handleDeleteChannel = (index: number) => {
        const draft = drafts[index];
        if (!draft.id) {
            setDrafts((prev) => prev.filter((_, i) => i !== index));
            return;
        }
        setSavingIndex(index);
        deleteNotificationChannel(draft.id)
            .catch(handleError)
            .then(checkStatus)
            .then(() => {
                setSavingIndex(null);
                showToast('Notification channel removed.', ToastSuccess);
                loadData();
            })
            .catch(() => {
                setSavingIndex(null);
                showToast('Failed to remove notification channel.');
            });
    };

    if (!businessId) {
        return (
            <Paper className={classes.root}>
                <Typography color="textSecondary">
                    Select a business from the top bar to configure supplier notifications.
                </Typography>
            </Paper>
        );
    }

    return (
        <Paper className={classes.root}>
            <Typography variant="h6" gutterBottom>Supplier Notification Configuration</Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
                Email channels for the stock-order supplier notification. Recipient, subject and
                body accept Django template syntax with the notification context (e.g.
                <code>{'{{ supplier.order_email }}'}</code>, <code>{'{{ stock_order.number }}'}</code>).
                Selected attachment types are generated as purchase-order documents.
            </Typography>
            {!eventTypeId && (
                <Typography variant="body2" color="error" gutterBottom>
                    No notification event type exists for this business yet — it will be created
                    automatically when you save the first channel.
                </Typography>
            )}

            {isLoading && <CircularProgress size={22} />}

            {drafts.map((draft, index) => (
                <div className={classes.channelCard} key={draft.id || `draft-${index}`}>
                    <div className={classes.row}>
                        <Select
                            value={draft.channel}
                            onChange={(e) => updateDraft(index, { channel: e.target.value as string })}
                        >
                            {CHANNEL_TYPES.map((c) => (
                                <MenuItem key={c} value={c}>{c}</MenuItem>
                            ))}
                        </Select>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={draft.enabled}
                                    onChange={(e) => updateDraft(index, { enabled: e.target.checked })}
                                />
                            }
                            label="Enabled"
                        />
                    </div>
                    <TextField
                        className={classes.field}
                        label="Sender"
                        fullWidth
                        value={draft.sender}
                        onChange={(e) => updateDraft(index, { sender: e.target.value })}
                    />
                    <TextField
                        className={classes.field}
                        label="Recipients (one template per line)"
                        fullWidth
                        multiline
                        rows={2}
                        value={draft.to}
                        onChange={(e) => updateDraft(index, { to: e.target.value })}
                    />
                    <TextField
                        className={classes.field}
                        label="Subject template"
                        fullWidth
                        value={draft.subjectTemplate}
                        onChange={(e) => updateDraft(index, { subjectTemplate: e.target.value })}
                    />
                    <TextField
                        className={classes.field}
                        label="Body template"
                        fullWidth
                        multiline
                        rows={6}
                        value={draft.bodyTemplate}
                        onChange={(e) => updateDraft(index, { bodyTemplate: e.target.value })}
                    />
                    <div>
                        <Typography variant="caption" color="textSecondary">Attachments</Typography>
                        <div className={classes.row}>
                            {ATTACHMENT_TYPES.map((type) => (
                                <FormControlLabel
                                    key={type}
                                    control={
                                        <Checkbox
                                            checked={draft.attachmentTypes.indexOf(type) > -1}
                                            onChange={() => toggleAttachmentType(index, type)}
                                        />
                                    }
                                    label={type}
                                />
                            ))}
                        </div>
                    </div>
                    <Divider />
                    <div className={classes.actionsRow}>
                        <Button
                            color="primary"
                            variant="contained"
                            disabled={savingIndex === index}
                            onClick={() => handleSaveChannel(index)}
                        >
                            {savingIndex === index ? <CircularProgress size={18} /> : 'Save'}
                        </Button>
                        <Button
                            disabled={savingIndex === index}
                            onClick={() => handleDeleteChannel(index)}
                        >
                            Delete
                        </Button>
                    </div>
                </div>
            ))}

            <Button color="primary" onClick={handleAddChannel}>+ Add Channel</Button>
        </Paper>
    );
};

const mapStateToProps = (state: any) => ({
    userData: state.userLocalData,
});

const mapDispatchToProps = (dispatch: any) => ({
    showToast: (toast: Toast) => dispatch(enqueueSnackbar(toast)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SupplierNotificationConfigScreen);
